/**
 * Socket.IO connection with session token auth.
 * Auto-fallback to 5-second polling when the socket is disconnected.
 * Exposes `isPolling` so the UI can show a latency indicator.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getSessionToken, getActiveNodeIndex } from '../lib/api';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:4000';
const POLL_INTERVAL = 5000;

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface SocketEvents {
  onNodeChanged?: (data: { activeNodeIndex: number; node?: unknown }) => void;
  onSessionIdle?: () => void;
  onNewResponse?: (data: { nodeId: string; responseCount: number }) => void;
  onPollResultsUpdated?: (data: unknown) => void;
  onNpsResultsUpdated?: (data: unknown) => void;
  onQuizResultsUpdated?: (data: unknown) => void;
}

interface UseSessionSocketReturn {
  connected: boolean;
  isPolling: boolean;
  socket: Socket | null;
  disconnect: () => void;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

export function useSessionSocket(
  sessionId: string | null,
  events: SocketEvents,
): UseSessionSocketReturn {
  const [connected, setConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastNodeIndexRef = useRef<number>(-1);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  /* -- Polling fallback ------------------------------------------ */
  const startPolling = useCallback(() => {
    if (pollTimerRef.current || !sessionId) return;

    setIsPolling(true);

    pollTimerRef.current = setInterval(async () => {
      try {
        const data = await getActiveNodeIndex(sessionId);
        if (data.activeNodeIndex !== lastNodeIndexRef.current) {
          lastNodeIndexRef.current = data.activeNodeIndex;
          eventsRef.current.onNodeChanged?.({
            activeNodeIndex: data.activeNodeIndex,
          });
        }
      } catch (err) {
        console.warn('Polling error:', err);
      }
    }, POLL_INTERVAL);
  }, [sessionId]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  /* -- Socket connection ----------------------------------------- */
  useEffect(() => {
    if (!sessionId) return;

    const token = getSessionToken();
    if (!token) return;

    const socket = io(API_BASE, {
      auth: { token },
      query: { sessionId },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      stopPolling();

      // Join the session room
      socket.emit('join-session', { sessionId });
    });

    socket.on('disconnect', () => {
      setConnected(false);
      startPolling();
    });

    socket.on('connect_error', () => {
      setConnected(false);
      startPolling();
    });

    // Event listeners
    socket.on('node-changed', (data: { activeNodeIndex: number; node?: unknown }) => {
      lastNodeIndexRef.current = data.activeNodeIndex;
      eventsRef.current.onNodeChanged?.(data);
    });

    socket.on('session-idle', () => {
      eventsRef.current.onSessionIdle?.();
    });

    socket.on('new-response', (data: { nodeId: string; responseCount: number }) => {
      eventsRef.current.onNewResponse?.(data);
    });

    // Batched form — the server coalesces response bursts (api lib/socketBatcher).
    socket.on('new-response-batch', (data: { items?: unknown[] }) => {
      for (const item of data?.items ?? []) {
        eventsRef.current.onNewResponse?.(item as { nodeId: string; responseCount: number });
      }
    });

    socket.on('poll-results-updated', (data: unknown) => {
      eventsRef.current.onPollResultsUpdated?.(data);
    });

    socket.on('nps-results-updated', (data: unknown) => {
      eventsRef.current.onNpsResultsUpdated?.(data);
    });

    socket.on('quiz-results-updated', (data: unknown) => {
      eventsRef.current.onQuizResultsUpdated?.(data);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      stopPolling();
      setConnected(false);
    };
  }, [sessionId, startPolling, stopPolling]);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    stopPolling();
    setConnected(false);
  }, [stopPolling]);

  return {
    connected,
    isPolling,
    socket: socketRef.current,
    disconnect,
  };
}
